import { useMemo } from 'react';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import ReservationService, {
  ChainDraftRatePlan,
  ChainDraftRatePlanApproval,
  ChainDraftRatePlanUpdate,
} from '~/services/ReservationService';

export type RatePlanStatus = 'content-approved' | 'draft';

export interface ChainRatePlanGroup {
  rate_plan_code: string | null;
  name: string;
  property_count: number;
  complete_count: number;
}

interface ChainRatePlansResponse {
  status: number;
  result: Array<ChainRatePlanGroup>;
  count: number;
  total?: number;
}

export interface ChainRatePlansFilters {
  status: RatePlanStatus;
  page: number;
  limit: number;
  ratePlanCode?: string;
  propertySearch?: string;
}

export const chainRatePlanQueryKeys = {
  all: ['chains', 'ratePlans'] as const,
  chain: (chainId: string) => [...chainRatePlanQueryKeys.all, chainId] as const,
  list: (chainId: string, filters: ChainRatePlansFilters) =>
    [...chainRatePlanQueryKeys.chain(chainId), filters] as const,
  drafts: (chainId: string) => [...chainRatePlanQueryKeys.all, 'drafts', chainId] as const,
  draftList: (chainId: string, filters: Omit<ChainRatePlansFilters, 'status'>) =>
    [...chainRatePlanQueryKeys.drafts(chainId), filters] as const,
} as const;

export function useGetChainRatePlans(chainIds: Array<string>, filters: ChainRatePlansFilters) {
  const queryResults = useQueries({
    queries: chainIds.map((id) => ({
      queryKey: chainRatePlanQueryKeys.list(id, filters),
      queryFn: ({ signal: _signal }) =>
        ReservationService.getRatePlansByChain(
          id,
          filters.status,
          filters.page,
          filters.limit,
          filters.ratePlanCode,
          filters.propertySearch,
          // TODO: pass signal to service once it supports AbortSignal
        ) as Promise<ChainRatePlansResponse>,
      enabled: chainIds.length > 0,
      placeholderData: (prev: ChainRatePlansResponse | undefined) => prev,
    })),
  });

  const isLoading = queryResults.some((q) => q.isLoading);
  const error = queryResults.find((q) => q.error)?.error ?? null;

  // TODO: Pagination is still approximate for multi-ID chains (e.g. ACCOR has 4 IDs).
  // Each query fires with the same page/limit, so totalCount is the sum of all chain totals.
  // A multi-chain-ID endpoint would fix this properly.
  const { ratePlanGroups, totalCount } = useMemo(() => {
    const groups = queryResults.flatMap((q) => q.data?.result ?? []);
    const total = queryResults.reduce((sum, q) => sum + (q.data?.total ?? q.data?.count ?? 0), 0);
    return { ratePlanGroups: groups, totalCount: total };
  }, [queryResults]);

  return { ratePlanGroups, totalCount, isLoading, error };
}

export interface ChainDraftRatePlansResponse {
  status: number;
  result: Array<ChainDraftRatePlan>;
  count: number;
  total: number;
}

export interface ChainDraftPreviewRow {
  id: string;
  current: Partial<ChainDraftRatePlan> | null;
  proposed: Partial<ChainDraftRatePlan> | null;
  ready: boolean;
  errors: Array<{ code: string; message: string }>;
}

export interface ChainDraftPreviewResponse {
  rows: Array<ChainDraftPreviewRow>;
  valid_count: number;
  invalid_count: number;
}

export interface ChainDraftBatchResponse {
  succeeded: Array<{ id: string; updated_at: string }>;
  failed: Array<{ id: string; error: { code: string; message: string } }>;
}

export function useGetChainDraftRatePlans(chainId: string, filters: Omit<ChainRatePlansFilters, 'status'>) {
  return useQuery({
    queryKey: chainRatePlanQueryKeys.draftList(chainId, filters),
    queryFn: () =>
      ReservationService.getChainDraftRatePlans(
        chainId,
        filters.page,
        filters.limit,
        filters.ratePlanCode,
        filters.propertySearch,
      ) as Promise<ChainDraftRatePlansResponse>,
    enabled: chainId.length > 0,
    placeholderData: (previous: ChainDraftRatePlansResponse | undefined) => previous,
  });
}

interface DraftPreviewVariables {
  chainId: string;
  updates: Array<ChainDraftRatePlanUpdate>;
}

interface DraftApproveVariables {
  chainId: string;
  plans: Array<ChainDraftRatePlanApproval>;
}

export function usePreviewChainDraftRatePlans() {
  return useMutation({
    mutationFn: ({ chainId, updates }: DraftPreviewVariables) =>
      ReservationService.previewChainDraftRatePlans(chainId, updates) as Promise<{
        status: number;
        result: ChainDraftPreviewResponse;
      }>,
  });
}

export function useSaveChainDraftRatePlans() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chainId, updates }: DraftPreviewVariables) =>
      ReservationService.saveChainDraftRatePlans(chainId, updates) as Promise<{
        status: number;
        result: ChainDraftBatchResponse;
      }>,
    onSuccess: (_response, { chainId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: chainRatePlanQueryKeys.chain(chainId) }),
        queryClient.invalidateQueries({ queryKey: chainRatePlanQueryKeys.drafts(chainId) }),
      ]),
  });
}

export function useApproveChainDraftRatePlans() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chainId, plans }: DraftApproveVariables) =>
      ReservationService.approveChainDraftRatePlans(chainId, plans) as Promise<{
        status: number;
        result: ChainDraftBatchResponse;
      }>,
    onSuccess: (_response, { chainId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: chainRatePlanQueryKeys.chain(chainId) }),
        queryClient.invalidateQueries({ queryKey: chainRatePlanQueryKeys.drafts(chainId) }),
      ]),
  });
}
